import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  DocumentIcon,
  PhotoIcon,
  FilmIcon,
  MusicalNoteIcon,
  CodeBracketIcon,
  ArchiveBoxIcon,
  DocumentTextIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useFileStore, type FileItem as FileItemType } from '../../stores/fileStore';
import { useUIStore } from '../../stores/uiStore';
import { filesApi } from '../../api/files';
import { useToast } from '../ui/Toaster';
import clsx from 'clsx';

interface FileItemProps {
  file: FileItemType;
  viewMode: 'grid' | 'list';
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return PhotoIcon;
  if (mimeType.startsWith('video/')) return FilmIcon;
  if (mimeType.startsWith('audio/')) return MusicalNoteIcon;
  if (mimeType.includes('pdf') || mimeType.includes('document')) return DocumentTextIcon;
  if (mimeType.includes('zip') || mimeType.includes('archive') || mimeType.includes('rar'))
    return ArchiveBoxIcon;
  if (
    mimeType.includes('javascript') ||
    mimeType.includes('typescript') ||
    mimeType.includes('json') ||
    mimeType.includes('html') ||
    mimeType.includes('css')
  )
    return CodeBracketIcon;
  return DocumentIcon;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}

export function FileItem({ file, viewMode }: FileItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const navigate = useNavigate();
  const { selectedIds, toggleSelection, updateFile } = useFileStore();
  const openContextMenu = useUIStore((s) => s.openContextMenu);
  const setPreviewFile = useUIStore((s) => s.setPreviewFile);
  const isSelected = selectedIds.includes(file.id);
  const toast = useToast();

  const IconComponent = getFileIcon(file.mimeType);
  const isMarkdownFile = file.mimeType === 'text/markdown' || file.name.endsWith('.md');

  // Track click to differentiate single/double click
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openContextMenu({ id: file.id, type: 'file' }, { x: e.clientX, y: e.clientY });
  };

  const handleStar = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await filesApi.update(file.id, { isStarred: !file.isStarred });
      updateFile(file.id, { isStarred: !file.isStarred });
    } catch {
      toast.error('Failed to update file');
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Si hay modificadores, toggle selection inmediatamente
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      toggleSelection(file.id);
      return;
    }

    // Para archivos markdown, usar delay para distinguir single/double click
    if (isMarkdownFile) {
      // Si ya hay un timeout pendiente, es un doble clic - cancelar
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
        return;
      }

      // Esperar un poco para ver si es doble clic
      clickTimeoutRef.current = setTimeout(() => {
        clickTimeoutRef.current = null;
        setPreviewFile(file);
      }, 200);
    } else {
      // Para otros archivos, abrir preview inmediatamente
      setPreviewFile(file);
    }
  };

  const handleDoubleClick = async () => {
    // Cancelar cualquier timeout de single click pendiente
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }

    // If it's a markdown file, open in editor
    if (isMarkdownFile) {
      navigate(`/documents/${file.id}`);
      return;
    }

    // Otherwise, download the file
    try {
      const { downloadUrl } = await filesApi.getDownloadUrl(file.id);
      window.open(downloadUrl, '_blank');
    } catch {
      toast.error('Failed to download file');
    }
  };

  // Drag handlers
  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    // Si el archivo actual está seleccionado, arrastrar todos los seleccionados
    // Si no, solo arrastrar este archivo
    const fileIds = isSelected && selectedIds.length > 0 ? selectedIds : [file.id];
    e.dataTransfer.setData('application/x-file-ids', JSON.stringify(fileIds));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  if (viewMode === 'list') {
    return (
      <div
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <motion.div
          whileTap={{ scale: 0.995 }}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          onContextMenu={handleContextMenu}
          className={clsx(
            'flex items-center gap-4 px-4 py-3 rounded-xl cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-slate-800',
            isSelected && 'bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-500',
            isDragging && 'opacity-50'
          )}
        >
        <div
          className={clsx(
            'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
            file.mimeType.startsWith('image/') ? 'bg-pink-100 dark:bg-pink-900/30' :
            file.mimeType.startsWith('video/') ? 'bg-purple-100 dark:bg-purple-900/30' :
            file.mimeType.startsWith('audio/') ? 'bg-yellow-100 dark:bg-yellow-900/30' :
            'bg-gray-100 dark:bg-slate-700'
          )}
        >
          <IconComponent
            className={clsx(
              'w-5 h-5',
              file.mimeType.startsWith('image/') ? 'text-pink-600 dark:text-pink-400' :
              file.mimeType.startsWith('video/') ? 'text-purple-600 dark:text-purple-400' :
              file.mimeType.startsWith('audio/') ? 'text-yellow-600 dark:text-yellow-400' :
              'text-gray-500 dark:text-slate-400'
            )}
          />
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            {formatFileSize(file.size)} • {formatDate(file.updatedAt)}
          </p>
        </div>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleStar}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
        >
          {file.isStarred ? (
            <StarSolidIcon className="w-5 h-5 text-yellow-400" />
          ) : (
            <StarIcon className="w-5 h-5 text-gray-300 dark:text-slate-500 hover:text-yellow-400" />
          )}
        </motion.button>
        </motion.div>
      </div>
    );
  }

  // Grid view - diseño compacto horizontal (como carpetas)
  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <motion.div
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.98 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        className={clsx(
          'relative flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all',
          isSelected
            ? 'bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-500 shadow-md'
            : 'bg-white dark:bg-slate-800 hover:shadow-md border border-gray-100 dark:border-slate-700',
          isDragging && 'opacity-50'
        )}
      >
        {/* Hover checkbox - en la esquina derecha */}
        {isHovered && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute top-2 right-2 z-10"
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSelection(file.id)}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 dark:bg-slate-700"
            />
          </motion.div>
        )}

        {/* Star indicator */}
        {file.isStarred && !isHovered && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-2 right-2"
          >
            <StarSolidIcon className="w-4 h-4 text-yellow-400" />
          </motion.div>
        )}

        {/* File icon */}
        <div
          className={clsx(
            'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
            file.mimeType.startsWith('image/') ? 'bg-pink-100 dark:bg-pink-900/30' :
            file.mimeType.startsWith('video/') ? 'bg-purple-100 dark:bg-purple-900/30' :
            file.mimeType.startsWith('audio/') ? 'bg-yellow-100 dark:bg-yellow-900/30' :
            file.mimeType.includes('pdf') ? 'bg-red-100 dark:bg-red-900/30' :
            'bg-gray-100 dark:bg-slate-700'
          )}
        >
          <IconComponent
            className={clsx(
              'w-5 h-5',
              file.mimeType.startsWith('image/') ? 'text-pink-600 dark:text-pink-400' :
              file.mimeType.startsWith('video/') ? 'text-purple-600 dark:text-purple-400' :
              file.mimeType.startsWith('audio/') ? 'text-yellow-600 dark:text-yellow-400' :
              file.mimeType.includes('pdf') ? 'text-red-600 dark:text-red-400' :
              'text-gray-500 dark:text-slate-400'
            )}
          />
        </div>

        {/* File name and size */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-900 dark:text-white truncate text-sm" title={file.name}>
            {file.name}
          </p>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            {formatFileSize(file.size)}
          </p>
        </div>
      </motion.div>
    </div>
  );
}
